import "./hazmat-detail-row.css";

import type { DropDownListChangeEvent } from "@tql/ui-components-react/kendo/dropdowns";
import type { TextBoxChangeEvent } from "@tql/ui-components-react/kendo/inputs";
import { Error, Label } from "@tql/ui-components-react/kendo/labels";
import { DropDownList } from "@tql/ui-components-react/tql/dropdowns";
import { Input, TextBox } from "@tql/ui-components-react/tql/inputs/beta";
import { useContext, useMemo } from "react";

import { QuoteDetailsContext } from "~/features/quote/context/quote-details-context.tsx";
import type { HazmatClass, HazmatGroup } from "~/shared/schemas/hazmat-schema";
import type { HandlingUnit } from "~/shared/types/quote-details-types";

type HazmatDetailsProps = {
	handlingUnit: HandlingUnit;
	hazmatClasses: HazmatClass[] | undefined;
	hazmatGroups: HazmatGroup[] | undefined;
	validationErrors?: Set<string>;
	onClearFieldError?: (itemId: number, fieldKey: string) => void;
	hideSavedCommoditiesNote?: boolean;
};

export const HazmatDetailRow = ({
	handlingUnit,
	hazmatClasses,
	hazmatGroups,
	validationErrors,
	onClearFieldError,
	hideSavedCommoditiesNote = false,
}: HazmatDetailsProps) => {
	const { updateItem } = useContext(QuoteDetailsContext);

	const displayHazmatGroups: HazmatGroup[] | undefined = useMemo(() => {
		return hazmatGroups?.map((g) => ({
			...g,
			displayCode: g.code === "None" ? "N/A" : g.code,
		}));
	}, [hazmatGroups]);

	const unNumberError = validationErrors?.has("unNumber") ?? false;
	const properShippingNameError =
		validationErrors?.has("properShippingName") ?? false;
	const hazmatClassError = validationErrors?.has("hazmatClass") ?? false;
	const packingGroupError = validationErrors?.has("packingGroupId") ?? false;

	return (
		<div>
			<hr className={"tql-divider"} />
			{hideSavedCommoditiesNote ? null : (
				<div className={"hazmat-item-title"}>
					Hazmat Fields not included in Saved Commodities
				</div>
			)}
			<div className={"hazmat-item-wrapper"}>
				<div className={"k-form-field"}>
					<Label editorId={`un-number-${handlingUnit.id}`}>
						UN Number <span className={"required"}>&nbsp;*</span>
					</Label>
					<TextBox
						id={`un-number-${handlingUnit.id}`}
						onChange={(e: TextBoxChangeEvent) => {
							onClearFieldError?.(handlingUnit.id, "unNumber");
							updateItem(handlingUnit, "unNumber", e.value as string);
						}}
						valid={!unNumberError}
						value={handlingUnit.unNumber ?? ""}
					/>
					{unNumberError ? <Error>Required</Error> : null}
				</div>
				<div className={"k-form-field"}>
					<Label editorId={`proper-shipping-name-${handlingUnit.id}`}>
						Proper Shipping Name <span className={"required"}>&nbsp;*</span>
					</Label>
					<Input
						id={`proper-shipping-name-${handlingUnit.id}`}
						onChange={({ value }: { value: string }) => {
							onClearFieldError?.(handlingUnit.id, "properShippingName");
							updateItem(handlingUnit, "properShippingName", value);
						}}
						valid={!properShippingNameError}
						value={handlingUnit.properShippingName ?? ""}
					/>
					{properShippingNameError ? <Error>Required</Error> : null}
				</div>
				<div className={"k-form-field"}>
					<Label editorId={`hazmat-class-${handlingUnit.id}`}>
						Hazmat Class <span className={"required"}>&nbsp;*</span>
					</Label>
					<DropDownList
						data={hazmatClasses}
						dataItemKey={"id"}
						id={`hazmat-class-${handlingUnit.id}`}
						onChange={({ value }: { value: HazmatClass }) => {
							onClearFieldError?.(handlingUnit.id, "hazmatClass");
							updateItem(handlingUnit, "hazmatClass", value);
						}}
						textField={"code"}
						valid={!hazmatClassError}
						value={
							handlingUnit.hazmatClass ?? { id: 0, name: "select", code: "" }
						}
					/>
					{hazmatClassError ? <Error>Required</Error> : null}
				</div>
				<div className={"k-form-field"}>
					<Label editorId={`packing-group-${handlingUnit.id}`}>
						Packing Group <span className={"required"}>&nbsp;*</span>
					</Label>
					<DropDownList
						data={displayHazmatGroups}
						dataItemKey={"id"}
						id={`packing-group-${handlingUnit.id}`}
						onChange={(event: DropDownListChangeEvent) => {
							onClearFieldError?.(handlingUnit.id, "packingGroupId");
							updateItem(
								handlingUnit,
								"packingGroupId",
								event.value as HazmatGroup,
							);
						}}
						textField={"displayCode"}
						valid={!packingGroupError}
						value={
							handlingUnit.packingGroupId ?? {
								id: 0,
								name: "select",
								code: "",
								displayCode: "",
							}
						}
					/>
					{packingGroupError ? <Error>Required</Error> : null}
				</div>
			</div>
		</div>
	);
};
